import { useCallback, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  KommoLeadCardFieldsFragment,
  useKommoLeadCardQuery,
  useRefreshLeadMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import { useLeadErrorText, useLeadsNotify } from './leadsFeedback';
import { useLeadPermissions } from './useLeadPermissions';

/** ТЗ 0084 — карточка лида и «Обновить из Kommo». */

export type IKommoLeadCard = KommoLeadCardFieldsFragment;
export type ILeadContract = KommoLeadCardFieldsFragment['contracts'][number];
export type ILeadFundingCandidate = KommoLeadCardFieldsFragment['fundingCandidates'][number];

export const useKommoLead = (leadId: string | null) => {
  const { canUse } = useLeadPermissions();
  const skip = !canUse || !leadId;
  const { data, loading, error, refetch } = useKommoLeadCardQuery({
    variables: { id: leadId || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const lead = data?.kommoLead ?? null;
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    lead,
    loading: loading && !lead,
    failed: Boolean(error) && !lead,
    /** Ответ пришёл, а лида нет: удалён из зеркала или чужой источник. */
    notFound: Boolean(data) && !lead,
    reload,
  };
};

/**
 * Перечитать лида из Kommo сейчас. Ответ — та же запись `KommoLeadType:<id>`:
 * карточка и строка списка обновятся сами. Сервер перечитывает не чаще раза
 * в минуту — тогда `refreshed=false`, и человек узнаёт, что данные свежие.
 */
export const useRefreshKommoLead = () => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  const errorText = useLeadErrorText();
  const [refreshMut, { loading }] = useRefreshLeadMutation();
  const inFlight = useRef(false);

  const refresh = useCallback(
    async (id: string): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      try {
        const { data } = await refreshMut({ variables: { id } });
        const payload = data?.refreshKommoLead;
        const errors = (payload?.customErrors ?? []).filter(Boolean);
        if (!payload?.success || errors.length) {
          notify(
            ToastTypes.error,
            t('leads.toast.refreshFailed'),
            errors.map((error) => errorText(error)).join('\n') || undefined,
          );
          return false;
        }
        notify(
          ToastTypes.success,
          payload.refreshed
            ? t('leads.toast.refreshed')
            : t('leads.toast.refreshSkipped'),
        );
        return true;
      } catch (caught) {
        notify(
          ToastTypes.error,
          t('leads.toast.refreshFailed'),
          errorText({
            code:
              caught instanceof ApolloError && caught.graphQLErrors.length > 0
                ? 'NO_ACCESS'
                : 'NETWORK',
            message: '',
          }),
        );
        return false;
      } finally {
        inFlight.current = false;
      }
    },
    [errorText, notify, refreshMut, t],
  );

  return { refresh, refreshing: loading };
};
